import { useEffect, useRef } from 'react';
import { Card, Button } from 'react-bootstrap';
import { BsPlayFill, BsPauseFill } from 'react-icons/bs';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { togglePlayPause, stopRecording } from '../../store/slices/playerSlice';
import { formatPhoneLocal } from '../../utils/phone';

// Native <audio> driven by Redux state — react-player was ~80 KB of
// extra bundle just to wrap this single audio element.
export default function FloatingAudioPlayer() {
  const dispatch = useAppDispatch();
  const { url, playing, callerNumber } = useAppSelector((s) => s.player);
  const localCountryPrefix = useAppSelector((s) => s.ui.localCountryPrefix);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Mirror Redux playing/url state onto the <audio> element. We don't
  // dispatch on native play/pause events because the Redux state is
  // authoritative — button clicks already flip it, and other UI
  // surfaces may start/stop playback via the same actions.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.play().catch(() => { /* autoplay blocked — ignore */ });
    else a.pause();
  }, [playing, url]);

  if (!url) return null;

  return (
    <Card className="hcf-floating-player shadow">
      <Card.Body className="p-2">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <small className="fw-bold text-truncate me-2">
            Poziv: {formatPhoneLocal(callerNumber, localCountryPrefix)}
          </small>
          <Button
            variant="link"
            size="sm"
            className="p-0 text-danger"
            onClick={() => dispatch(stopRecording())}
            title="Zatvori"
          >
            ✕
          </Button>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => dispatch(togglePlayPause())}
            aria-label={playing ? 'Pauziraj' : 'Pusti'}
          >
            {playing ? <BsPauseFill size={14} /> : <BsPlayFill size={14} />}
          </Button>
          <audio
            ref={audioRef}
            src={url}
            controls
            // "auto" so bytes start downloading the moment this element
            // mounts — by the time React's useEffect above calls .play()
            // the browser already has enough data buffered for instant
            // start. "metadata" (the old value) only fetched duration,
            // forcing a 3–4 s cold fetch on the first play() call.
            preload="auto"
            style={{ width: '100%', height: 32 }}
            onEnded={() => dispatch(stopRecording())}
          />
        </div>
      </Card.Body>
    </Card>
  );
}
